import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Tooltip,
  Chip,
  Stack,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Edit as EditIcon,
} from "@mui/icons-material";

import { useFileUpload } from "../../../hooks/useFileUpload";
import { FileService } from "../../../services/staffandplant/plantService";

const FileUploadSection = ({
  documents = [],
  onDocumentsChange,
  disabled = false,
}) => {
  const { uploadFiles, uploading, uploadError } = useFileUpload();
  const [previewUrls, setPreviewUrls] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  
  const [editingDoc, setEditingDoc] = useState(null);
  const [editDocName, setEditDocName] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    const loadPreviewUrls = async () => {
      const newPreviewUrls = { ...previewUrls };

      for (const doc of documents) {
        const docId = doc.id || doc.upload_id;
        if (docId && !newPreviewUrls[docId]) {
          const lowerName = doc.name ? doc.name.toLowerCase() : "";
          const isPreviewable = 
            lowerName.includes(".jpg") ||
            lowerName.includes(".jpeg") ||
            lowerName.includes(".png") ||
            lowerName.includes(".gif") ||
            lowerName.includes(".webp") ||
            lowerName.includes(".bmp") ||
            lowerName.includes(".pdf");

          if (isPreviewable) {
            try {
              const previewUrl = await FileService.getFileBlob(docId, true);
              if (previewUrl) {
                newPreviewUrls[docId] = previewUrl;
              }
            } catch (error) {
              console.error("Error getting preview URL:", error);
            }
          }
        }
      }

      setPreviewUrls(newPreviewUrls);
    };

    if (documents.length > 0) {
      loadPreviewUrls();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  const handleEditDocName = (docIndex) => {
    const doc = documents[docIndex];
    setEditingDoc(docIndex);
    setEditDocName(doc.name || "");
    setEditDialogOpen(true);
  };

  const handleSaveDocName = async () => {
    if (editingDoc !== null && editDocName.trim()) {
      const updatedDocs = [...documents];
      const originalDoc = updatedDocs[editingDoc];
      
      updatedDocs[editingDoc] = {
        ...originalDoc,
        name: editDocName.trim(),
      };
      
      onDocumentsChange(updatedDocs);
      setEditDialogOpen(false);
      setEditingDoc(null);
      setEditDocName("");
    }
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingDoc(null);
    setEditDocName("");
  };

  const handleFileUpload = async (files) => {
    if (files.length === 0) return;


    try {
      const results = await uploadFiles(files);

      if (results.successful.length > 0) {
        const newDocuments = [...documents, ...results.successful];

        onDocumentsChange(newDocuments);

        const newPreviewUrls = { ...previewUrls };
        for (const doc of results.successful) {
          const docId = doc.id || doc.upload_id;
          const lowerName = doc.name ? doc.name.toLowerCase() : "";
          const isPreviewable = 
            lowerName.includes(".jpg") ||
            lowerName.includes(".jpeg") ||
            lowerName.includes(".png") ||
            lowerName.includes(".gif") ||
            lowerName.includes(".webp") ||
            lowerName.includes(".bmp") ||
            lowerName.includes(".pdf");

          if (docId && isPreviewable) {
            try {
              const previewUrl = await FileService.getFileBlob(docId, true);
              if (previewUrl) {
                newPreviewUrls[docId] = previewUrl;
              }
            } catch (error) {
              console.error("Error getting preview URL:", error);
            }
          }
        }
        setPreviewUrls(newPreviewUrls);
      }

      if (results.failed.length > 0) {
        console.error("Some files failed to upload:", results.failed);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    }
  };

  const handleInputChange = (event) => {
    const files = Array.from(event.target.files);
    handleFileUpload(files);
    event.target.value = "";
  };

  const handleRemoveDocument = (index) => {
    const newDocuments = documents.filter((_, i) => i !== index);
    onDocumentsChange(newDocuments);
  };

  const handleAddMoreClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = ".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,.csv,.xlsx,.xls,.doc,.docx,.txt";
    input.onchange = handleInputChange;
    input.click();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !uploading) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled || uploading) return;

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter((file) => {
      const lowerType = file.type.toLowerCase();
      const lowerName = file.name.toLowerCase();
      return (
        lowerType.includes("image/") ||
        lowerName.endsWith(".pdf") ||
        lowerName.endsWith(".csv") ||
        lowerName.endsWith(".xlsx") ||
        lowerName.endsWith(".xls") ||
        lowerName.endsWith(".doc") ||
        lowerName.endsWith(".docx") ||
        lowerName.endsWith(".txt")
      );
    });

    if (validFiles.length > 0) {
      handleFileUpload(validFiles);
    }
  };

  const getFileIcon = (fileName) => {
    if (!fileName) return <FileIcon />;

    const extension = fileName.toLowerCase();
    if (extension.includes(".pdf")) {
      return <PdfIcon sx={{ color: "#d32f2f" }} />;
    } else if (
      extension.includes(".jpg") ||
      extension.includes(".jpeg") ||
      extension.includes(".png") ||
      extension.includes(".gif") ||
      extension.includes(".webp") ||
      extension.includes(".bmp")
    ) {
      return <ImageIcon sx={{ color: "#2196f3" }} />;
    } else if (
      extension.includes(".csv") ||
      extension.includes(".xls") ||
      extension.includes(".xlsx")
    ) {
      return <ExcelIcon sx={{ color: "#1976D2" }} />;
    } else if (
      extension.includes(".doc") ||
      extension.includes(".docx")
    ) {
      return <FileIcon sx={{ color: "#ff6f00" }} />;
    }
    return <FileIcon sx={{ color: "#757575" }} />;
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Documents
        </Typography>
        <Chip
          label={documents.length}
          size="small"
          sx={{
            bgcolor: "#f0f0f0",
            color: "#666",
            fontWeight: 500,
            minWidth: 28,
            height: 20,
          }}
        />
        <Tooltip title="Upload plant-related documents such as certificates, manuals, etc.">
          <Box
            component="span"
            sx={{
              ml: 0.5,
              color: "text.secondary",
              fontSize: 16,
              cursor: "help",
              "&:hover": { color: "text.primary" },
            }}
          >
            ⓘ
          </Box>
        </Tooltip>
      </Stack>

      <Box
        component="label"
        sx={{
          p: 3,
          textAlign: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          bgcolor: isDragOver ? "#f8fff8" : "transparent",
          transition: "all 0.2s ease",
          opacity: disabled ? 0.6 : 1,
          width: "100%",
          boxSizing: "border-box",
          borderRadius: 2,
          "&:hover": {
            bgcolor: disabled
              ? "transparent"
              : isDragOver
              ? "#f8fff8"
              : "#f9f9f9",
          },
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,.csv,.xlsx,.xls,.doc,.docx,.txt"
          style={{ display: "none" }}
          onChange={handleInputChange}
          disabled={disabled || uploading}
        />

        <Stack alignItems="center" spacing={2}>
          <Box sx={{ position: "relative" }}>
            <CloudUploadIcon
              sx={{
                fontSize: 64,
                color: isDragOver ? "#4caf50" : uploading ? "#2196f3" : "#999",
                transition: "color 0.2s ease",
              }}
            />
            {uploading && (
              <CircularProgress
                size={68}
                sx={{
                  position: "absolute",
                  top: -2,
                  left: -2,
                  color: "#2196f3",
                }}
              />
            )}
          </Box>

          <Stack alignItems="center" spacing={1}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDragOver ? "#4caf50" : "#333",
                transition: "color 0.2s ease",
              }}
            >
              {uploading
                ? "Uploading files..."
                : isDragOver
                ? "Drop files here"
                : "Upload Documents"}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ maxWidth: 300 }}
            >
              {isDragOver
                ? "Release to upload your files"
                : "Drag and drop files here or click to browse"}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              justifyContent="center"
            >
              <Chip
                label="JPG"
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, height: 24 }}
              />
              <Chip
                label="PNG"
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, height: 24 }}
              />
              <Chip
                label="PDF"
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, height: 24 }}
              />
              <Chip
                label="CSV"
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, height: 24 }}
              />
              <Chip
                label="XLSX"
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, height: 24 }}
              />
              <Chip
                label="DOC"
                size="small"
                variant="outlined"
                sx={{ fontSize: 11, height: 24 }}
              />
            </Stack>
          </Stack>
        </Stack>

        {uploadError && (
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "#ffebee",
              border: "1px solid #ffcdd2",
              borderRadius: 2,
            }}
          >
            <Typography color="error" variant="body2" sx={{ fontWeight: 500 }}>
              {uploadError}
            </Typography>
          </Paper>
        )}

        {documents && documents.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Uploaded Files
            </Typography>
            <Stack spacing={1}>
              {documents.map((doc, index) => {
                const docId = doc.id || doc.upload_id;
                const docName =
                  doc.name || doc.filename || `Document ${index + 1}`;

                return (
                  <Paper
                    key={`${docId}-${index}`}
                    elevation={0}
                    sx={{
                      border: "1px solid #e8e8e8",
                      borderRadius: 2,
                      p: 2,
                      bgcolor: "#fdfdfd",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        bgcolor: "#f8f8f8",
                        borderColor: "#d0d0d0",
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      {previewUrls[docId] ? (
                        <Paper
                          elevation={0}
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "1px solid #e0e0e0",
                            flexShrink: 0,
                          }}
                        >
                          <Box
                            component="img"
                            src={previewUrls[docId]}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            alt={docName}
                          />
                        </Paper>
                      ) : (
                        <Paper
                          elevation={0}
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            border: "1px solid #e0e0e0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#f8f9fa",
                            flexShrink: 0,
                          }}
                        >
                          {getFileIcon(docName)}
                        </Paper>
                      )}

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            mb: 0.5,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {docName}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          ID: {docId}
                        </Typography>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={docName}
                            size="small"
                            sx={{
                              bgcolor: "#E3F2FD",
                              color: "#1976D2",
                              maxWidth: 200,
                              "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              },
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleEditDocName(index)}
                            sx={{
                              color: "#666",
                              "&:hover": {
                                color: "#333",
                                bgcolor: "#f0f0f0",
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Box>

                      <IconButton
                        onClick={() => handleRemoveDocument(index)}
                        disabled={disabled}
                        size="small"
                        sx={{
                          color: "#999",
                          "&:hover": {
                            color: "#d32f2f",
                            bgcolor: "#ffebee",
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        )}

        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Max file size: 10MB per file
          </Typography>
          <Button
            size="small"
            onClick={handleAddMoreClick}
            sx={{
              color: "#666",
              "&:hover": {
                color: "#333",
                bgcolor: "rgba(0,0,0,0.04)",
              },
              fontWeight: 600,
              textTransform: "none",
            }}
            disabled={disabled || uploading}
          >
            + Add More Files
          </Button>
        </Box>
      </Box>

      {/* Edit Document Name Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Document Name</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Document Name"
            value={editDocName}
            onChange={(e) => setEditDocName(e.target.value)}
            margin="dense"
            variant="outlined"
            placeholder="Enter document name"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Cancel</Button>
          <Button 
            onClick={handleSaveDocName} 
            variant="contained"
            disabled={!editDocName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FileUploadSection;